import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator, TextInput } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', stages:'/api/data/crm_stages', activities:'/api/data/crm_activities', employees:'/api/employees' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function PipelineManagementScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState('kanban');
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pipeAnalytics, setPipeAnalytics] = useState({ totalValue:0, weightedValue:0, avgDealSize:0, avgProbability:0, stageCount:{}, velocity:0, winRate:0, lossRate:0 });

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,dealsRes,stagesRes,employeesRes,activitiesRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.stages),api.get(EP.employees,{params:p}),api.get(EP.activities,{params:p}),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const rawDeals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const stgs=safeData(stagesRes.status==='fulfilled'?stagesRes.value:null);
    const emps=safeData(employeesRes.status==='fulfilled'?employeesRes.value:null);

    const stageList=stgs.length>0?stgs:[{stage_id:'lead',name:'Lead',color:'#94a3b8',probability_weight:10},{stage_id:'contacted',name:'Contacted',color:'#3b82f6',probability_weight:25},{stage_id:'qualified',name:'Qualified',color:'#f59e0b',probability_weight:50},{stage_id:'proposal',name:'Proposal',color:'#8b5cf6',probability_weight:65},{stage_id:'negotiation',name:'Negotiation',color:'#ec4899',probability_weight:80},{stage_id:'closed_won',name:'Won',color:'#10b981',probability_weight:100},{stage_id:'closed_lost',name:'Lost',color:'#ef4444',probability_weight:0}];
    setStages(stageList);
    setDeals(rawDeals);
    setEmployees(emps);

    const won=rawDeals.filter(d=>d.stage==='closed_won');
    const lost=rawDeals.filter(d=>d.stage==='closed_lost');
    const open=rawDeals.filter(d=>!['closed_won','closed_lost'].includes(d.stage));
    const totalVal=rawDeals.reduce((s,d)=>s+parseFloat(d.value||0),0);
    const weightedVal=rawDeals.reduce((s,d)=>s+parseFloat(d.value||0)*(d.probability||0)/100,0);
    const stageCount={};rawDeals.forEach(d=>{stageCount[d.stage||'unknown']=(stageCount[d.stage||'unknown']||0)+1;});
    setPipeAnalytics({totalValue:totalVal,weightedValue:weightedVal,avgDealSize:rawDeals.length>0?totalVal/rawDeals.length:0,
      avgProbability:rawDeals.length>0?rawDeals.reduce((s,d)=>s+(d.probability||0),0)/rawDeals.length:0,
      stageCount,velocity:open.length>0?Math.round(open.reduce((s,d)=>s+Math.max(1,Math.floor((Date.now()-new Date(d.created_at).getTime())/(1000*60*60*24))),0)/open.length):0,
      winRate:rawDeals.length>0?(won.length/rawDeals.length*100):0,lossRate:rawDeals.length>0?(lost.length/rawDeals.length*100):0,
      totalValue:totalVal});
  };

  const moveDeal=async(deal,newStage)=>{
    try{await api.put(`${EP.deals}/${deal.id}`,{stage:newStage});Alert.alert('Moved',`Deal moved to ${newStage}`);await fetchAll(companyId);}catch(e){Alert.alert('Error',e.message);}
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const getStageColor=s=>({lead:'#6B7280',contacted:'#3B82F6',qualified:'#F59E0B',proposal:'#8B5CF6',negotiation:'#EC4899',closed_won:'#10B981',closed_lost:'#EF4444'}[s]||'#6B7280');

  const renderHeader=()=>(
    <LinearGradient colors={['#0F172A','#1E293B']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.hBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={{flex:1,marginLeft:12}}><Text style={st.hTitle}>{companyProfile?.name||'Pipeline Management'}</Text>
          <Text style={st.hSub}>{deals.length} deals · {formatCurrency(pipeAnalytics.totalValue)} total</Text></View>
        <TouchableOpacity onPress={()=>setViewMode(viewMode==='kanban'?'list':'kanban')} style={st.hBtn}>
          <MaterialCommunityIcons name={viewMode==='kanban'?'format-list-bulleted':'view-column'} size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderStats=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsBar} contentContainerStyle={{paddingHorizontal:16}}>
      {[{l:'Total',v:formatCurrency(pipeAnalytics.totalValue),c:'#0F172A'},{l:'Weighted',v:formatCurrency(pipeAnalytics.weightedValue),c:'#10B981'},
        {l:'Avg Deal',v:formatCurrency(pipeAnalytics.avgDealSize),c:'#3B82F6'},{l:'Win Rate',v:`${pipeAnalytics.winRate.toFixed(1)}%`,c:'#8B5CF6'},
        {l:'Avg Days',v:String(pipeAnalytics.velocity),c:'#F59E0B'}].map((s,i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statVal,{color:s.c}]}>{s.v}</Text><Text style={st.statLbl}>{s.l}</Text></View>
      ))}
    </ScrollView>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'overview',t:'Pipeline',i:'pipe'},{k:'velocity',t:'Velocity',i:'speedometer'},{k:'conversion',t:'Conversion',i:'filter-variant'},{k:'team',t:'By Rep',i:'account-group'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#0F172A':'#6B7280'}/>
          <Text style={[st.tabTxt,activeTab===tab.k&&{color:'#0F172A',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderKanban=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{padding:16}}>
      {stages.filter(s=>!['closed_lost'].includes(s.stage_id)).map((stg,idx)=>{
        const stgDeals=deals.filter(d=>d.stage===stg.stage_id);
        const stgVal=stgDeals.reduce((s,d)=>s+parseFloat(d.value||0),0);
        return (
          <View key={idx} style={[st.kanbanCol,{borderTopColor:stg.color||'#6B7280'}]}>
            <View style={st.kanbanHeader}>
              <Text style={st.kanbanName}>{stg.name}</Text>
              <View style={[st.badge,{backgroundColor:(stg.color||'#6B7280')+'20'}]}><Text style={[st.badgeTxt,{color:stg.color}]}>{stgDeals.length}</Text></View>
            </View>
            <Text style={st.kanbanVal}>{formatCurrency(stgVal)}</Text>
            {stgDeals.map(d=>(
              <TouchableOpacity key={d.id} style={st.kanbanCard} onPress={()=>{setSelected(d);setShowModal(true);}}>
                <Text style={st.kCardName}>{d.contact_name||'Unknown'}</Text>
                <Text style={st.kCardCo}>{d.company||''}</Text>
                {parseFloat(d.value||0)>0&&<Text style={st.kCardVal}>{formatCurrency(d.value)}</Text>}
                <Text style={st.kCardProb}>{d.probability||0}% prob</Text>
              </TouchableOpacity>
            ))}
          </View>
        );
      })}
    </ScrollView>
  );

  const renderVelocity=()=>(
    <View style={st.section}><Text style={st.secTitle}>Pipeline Velocity</Text>
      {stages.filter(s=>!['closed_won','closed_lost'].includes(s.stage_id)).map((stg,idx)=>{
        const stgDeals=deals.filter(d=>d.stage===stg.stage_id);
        const avgDays=stgDeals.length>0?Math.round(stgDeals.reduce((s,d)=>s+Math.max(1,Math.floor((Date.now()-new Date(d.updated_at||d.created_at).getTime())/(1000*60*60*24))),0)/stgDeals.length):0;
        return (
          <View key={idx} style={st.velRow}>
            <View style={{flexDirection:'row',alignItems:'center',width:120}}>
              <View style={[st.dot,{backgroundColor:stg.color||'#6B7280'}]}/><Text style={st.velName}>{stg.name}</Text></View>
            <View style={st.velBar}><View style={[st.velFill,{width:`${Math.min((avgDays/30)*100,100)}%`,backgroundColor:avgDays>14?'#EF4444':avgDays>7?'#F59E0B':'#10B981'}]}/></View>
            <Text style={st.velDays}>{avgDays}d avg</Text>
          </View>
        );
      })}
    </View>
  );

  const renderConversion=()=>{
    const stageOrder=['lead','contacted','qualified','proposal','negotiation','closed_won'];
    const convData=stageOrder.map(sid=>({name:stages.find(s=>s.stage_id===sid)?.name||sid,count:deals.filter(d=>d.stage===sid).length,color:getStageColor(sid)}));
    return (
      <View style={st.section}><Text style={st.secTitle}>Stage Conversion Funnel</Text>
        {convData.map((f,i)=>(
          <View key={i} style={{marginBottom:12}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
              <Text style={st.convName}>{f.name}</Text><Text style={st.convCount}>{f.count} deals</Text></View>
            <View style={[st.convBar,{backgroundColor:f.color+'20'}]}><View style={[st.convFill,{width:`${deals.length>0?(f.count/deals.length*100):0}%`,backgroundColor:f.color}]}/></View>
            {i<convData.length-1&&convData[i+1].count>0&&<Text style={st.convRate}>{f.count>0?`${((convData[i+1].count/f.count)*100).toFixed(0)}% → ${convData[i+1].name}`:'—'}</Text>}
          </View>
        ))}
      </View>
    );
  };

  const renderByRep=()=>{
    const repMap={};
    deals.forEach(d=>{const rep=d.assigned_to?`Rep #${d.assigned_to}`:'Unassigned';if(!repMap[rep])repMap[rep]={count:0,value:0,won:0};
      repMap[rep].count++;repMap[rep].value+=parseFloat(d.value||0);if(d.stage==='closed_won')repMap[rep].won++;});
    return (
      <View style={st.section}><Text style={st.secTitle}>Pipeline by Rep</Text>
        {Object.keys(repMap).length===0?<View style={st.empty}><Text style={st.emptyTxt}>No data</Text></View>:
          Object.entries(repMap).sort((a,b)=>b[1].value-a[1].value).map(([rep,data],i)=>{
            const emp=employees.find(e=>String(e.id)===rep.replace('Rep #',''));
            const name=emp?`${emp.first_name||''} ${emp.last_name||''}`.trim():rep;
            return (
              <View key={i} style={st.repCard}>
                <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
                  <Text style={st.repName}>{name}</Text>
                  <Text style={st.repVal}>{formatCurrency(data.value)}</Text></View>
                <Text style={st.repMeta}>{data.count} deals · {data.won} won</Text>
              </View>
            );
          })
        }
      </View>
    );
  };

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0F172A']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){
      case 'overview':return viewMode==='kanban'?renderKanban():wrap(<View style={{padding:16}}>{deals.map((d,i)=>(
        <TouchableOpacity key={i} style={st.listCard} onPress={()=>{setSelected(d);setShowModal(true);}}>
          <View style={{flexDirection:'row',justifyContent:'space-between'}}><View style={{flex:1}}>
            <Text style={st.kCardName}>{d.contact_name||'Unknown'}</Text><Text style={st.kCardCo}>{d.company} · {d.stage}</Text></View>
            <Text style={st.kCardVal}>{formatCurrency(d.value)}</Text></View></TouchableOpacity>
      ))}</View>);
      case 'velocity':return wrap(renderVelocity());
      case 'conversion':return wrap(renderConversion());
      case 'team':return wrap(renderByRep());
      default:return null;
    }
  };

  const renderModal=()=>(
    <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>
      <View style={st.modal}><View style={st.modalH}><Text style={st.modalTitle}>{selected?.contact_name||'Deal'}</Text>
        <TouchableOpacity style={st.modalClose} onPress={()=>setShowModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
      {selected&&<ScrollView style={{flex:1,padding:16}}>
        <View style={st.mSec}><Text style={st.mSecTitle}>Deal Info</Text>
          <Text style={st.mTxt}>Company: {selected.company}</Text><Text style={st.mTxt}>Contact: {selected.contact_name}</Text>
          <Text style={st.mTxt}>Email: {selected.contact_email}</Text><Text style={st.mTxt}>Stage: {selected.stage}</Text>
          <Text style={st.mTxt}>Value: {formatCurrency(selected.value)}</Text><Text style={st.mTxt}>Probability: {selected.probability}%</Text></View>
        <View style={st.mSec}><Text style={st.mSecTitle}>Move Stage</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {['lead','contacted','qualified','proposal','negotiation','closed_won','closed_lost'].map(s2=>(
              <TouchableOpacity key={s2} style={[st.stageBtn,{borderColor:getStageColor(s2)}]} onPress={()=>{moveDeal(selected,s2);setShowModal(false);}}>
                <Text style={[st.stageBtnTxt,{color:getStageColor(s2)}]}>{s2.replace('closed_','')}</Text></TouchableOpacity>
            ))}
          </View></View>
        {companyProfile&&<View style={st.mSec}><Text style={st.mSecTitle}>Company</Text><Text style={st.mTxt}>{companyProfile.name}</Text></View>}
      </ScrollView>}</View>
    </Modal>
  );

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#0F172A"/><Text style={st.loadingTxt}>Loading pipeline...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderStats()}{renderTabs()}{renderContent()}{renderModal()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingTxt:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},hBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},hTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},hSub:{fontSize:13,color:'#94A3B8',marginTop:2},
  statsBar:{flexGrow:0,flexShrink:0,backgroundColor:'#FFF',paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:16,minWidth:70},statVal:{fontSize:16,fontWeight:'bold'},statLbl:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#F1F5F9'},tabTxt:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},secTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  dot:{width:10,height:10,borderRadius:5,marginRight:6},badge:{borderRadius:10,paddingHorizontal:8,paddingVertical:2},badgeTxt:{fontSize:11,fontWeight:'600'},
  kanbanCol:{width:width*0.7,backgroundColor:'#FFF',borderRadius:12,padding:12,marginRight:12,borderTopWidth:3,borderWidth:1,borderColor:'#E5E7EB'},
  kanbanHeader:{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:4},kanbanName:{fontSize:15,fontWeight:'600',color:'#1F2937'},kanbanVal:{fontSize:13,color:'#10B981',fontWeight:'600',marginBottom:8},
  kanbanCard:{backgroundColor:'#F9FAFB',borderRadius:8,padding:10,marginBottom:6},kCardName:{fontSize:14,fontWeight:'600',color:'#1F2937'},kCardCo:{fontSize:12,color:'#6B7280',marginTop:1},kCardVal:{fontSize:13,fontWeight:'700',color:'#10B981',marginTop:2},kCardProb:{fontSize:11,color:'#9CA3AF',marginTop:1},
  listCard:{backgroundColor:'#FFF',marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},
  velRow:{flexDirection:'row',alignItems:'center',marginBottom:10},velName:{fontSize:13,color:'#1F2937',marginLeft:6},velBar:{flex:1,height:6,backgroundColor:'#E5E7EB',borderRadius:3,marginHorizontal:8,overflow:'hidden'},velFill:{height:'100%',borderRadius:3},velDays:{fontSize:12,fontWeight:'600',color:'#1F2937',width:50,textAlign:'right'},
  convName:{fontSize:14,fontWeight:'500',color:'#1F2937'},convCount:{fontSize:13,color:'#6B7280'},convBar:{height:8,borderRadius:4,overflow:'hidden'},convFill:{height:'100%',borderRadius:4},convRate:{fontSize:11,color:'#9CA3AF',marginTop:2},
  empty:{alignItems:'center',padding:32},emptyTxt:{fontSize:16,color:'#6B7280',marginTop:12},
  repCard:{backgroundColor:'#F9FAFB',borderRadius:8,padding:12,marginBottom:8},repName:{fontSize:15,fontWeight:'600',color:'#1F2937'},repVal:{fontSize:14,fontWeight:'700',color:'#10B981'},repMeta:{fontSize:12,color:'#6B7280',marginTop:2},
  stageBtn:{borderWidth:1.5,borderRadius:8,paddingHorizontal:12,paddingVertical:6},stageBtnTxt:{fontSize:12,fontWeight:'600',textTransform:'capitalize'},
  modal:{flex:1,backgroundColor:'#FFF'},modalH:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  mSec:{marginBottom:20},mSecTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},mTxt:{fontSize:14,color:'#6B7280',lineHeight:22},
});